/**
 * DevDecisionEngine — developer view for the Decision Engine (Layer 3).
 *
 * Shows every step of the pipeline so we can inspect what Retail Ranker
 * decided and why:
 *   evidence → intents → observations → policies → ranked / rejected
 *
 * Not a customer surface. Route: /dev/decision-engine
 */

import { useMemo, useState } from "react";
import { allFixtures, type ReferenceStoreFixture } from "@/engine/harness/fixtures";
import { fixtureToDecisionInput } from "@/engine/decision/fixtures-adapter";
import { DeterministicDecisionEngine } from "@/engine/decision/deterministic-engine";
import { DEFAULT_POLICIES } from "@/engine/decision/policies";
import type { DecisionEngineOutput, PolicyEffect } from "@/engine/decision/types";

type RunState = {
  loading: boolean;
  error?: string;
  output?: DecisionEngineOutput;
  ms?: number;
};

export default function DevDecisionEngine() {
  const [fixtureSlug, setFixtureSlug] = useState<string>(allFixtures[0].slug);
  const [run, setRun] = useState<RunState>({ loading: false });

  const fixture: ReferenceStoreFixture =
    allFixtures.find((f) => f.slug === fixtureSlug) ?? allFixtures[0];

  const input = useMemo(() => fixtureToDecisionInput(fixture), [fixture]);

  async function runEngine() {
    setRun({ loading: true });
    const started = performance.now();
    try {
      const engine = new DeterministicDecisionEngine();
      const output = await engine.analyze(input);
      setRun({ loading: false, output, ms: Math.round(performance.now() - started) });
    } catch (e) {
      setRun({
        loading: false,
        error: e instanceof Error ? e.message : String(e),
        ms: Math.round(performance.now() - started),
      });
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground p-6 md:p-10">
      <div className="mx-auto max-w-[1400px] space-y-6">
        <header className="space-y-2">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            Retail Ranker · Developer tools
          </p>
          <h1 className="text-3xl font-semibold">Decision Engine — pipeline inspector</h1>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Deterministic. No LLM. Shows every step: evidence signals →
            customer intents → observations → decision policies → ranked
            opportunities + rejected candidates. This is where Retail Ranker's
            expertise lives.
          </p>
        </header>

        <section className="rounded-lg border border-border bg-card p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium">Reference store:</label>
            <select
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={fixtureSlug}
              onChange={(e) => {
                setFixtureSlug(e.target.value);
                setRun({ loading: false });
              }}
            >
              {allFixtures.map((f) => (
                <option key={f.slug} value={f.slug}>
                  {f.slug}
                </option>
              ))}
            </select>
            <button
              onClick={runEngine}
              disabled={run.loading}
              className="rounded-md bg-primary text-primary-foreground px-3 py-2 text-sm hover:opacity-90 disabled:opacity-50"
            >
              {run.loading ? "Running…" : "Run Decision Engine"}
            </button>
            {run.ms !== undefined && (
              <span className="text-xs text-muted-foreground">{run.ms} ms</span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <ContextList
              label="Strengths"
              items={input.business_context.strengths.map((t) => t.label)}
            />
            <ContextList
              label="Goals"
              items={input.business_context.goals.map((t) => t.label)}
            />
            <ContextList
              label="Constraints"
              items={input.business_context.constraints.map((t) => t.label)}
            />
          </div>
        </section>

        {run.error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive whitespace-pre-wrap">
            {run.error}
          </div>
        )}

        {run.output && (
          <>
            <PoliciesInPlay />

            <Section title={`Customer intents (${run.output.intents.length})`} subtitle="Layer 2 · Analysis — clustered from evidence, not keywords">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {run.output.intents.map((intent) => (
                  <div key={intent.intent_id} className="rounded-md border border-border p-3 text-sm">
                    <p className="font-medium">{intent.label}</p>
                    <p className="text-foreground/80">{intent.description}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      demand {intent.demand_strength.toFixed(2)} · {intent.trend} · local {intent.local_confidence.toFixed(2)} · {intent.evidence.length} signal(s)
                    </p>
                    {intent.tags && intent.tags.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">tags: {intent.tags.join(", ")}</p>
                    )}
                  </div>
                ))}
                {run.output.intents.length === 0 && (
                  <p className="italic text-muted-foreground text-sm">No intents clustered.</p>
                )}
              </div>
            </Section>

            <Section title={`Observations (${run.output.observations.length})`} subtitle="Layer 2 · Analysis — structural facts, never recommendations">
              <ul className="space-y-1 text-xs font-mono">
                {run.output.observations.map((o) => (
                  <li key={o.observation_id}>
                    · <span className="font-medium">{o.kind}</span> · {o.intent_id}
                    {"gap_magnitude" in o && ` · gap ${o.gap_magnitude.toFixed(2)} (presence: ${o.presence})`}
                    {"slope" in o && ` · slope ${o.slope.toFixed(2)} → ${o.horizon_hint}`}
                  </li>
                ))}
                {run.output.observations.length === 0 && (
                  <li className="italic text-muted-foreground">No observations.</li>
                )}
              </ul>
            </Section>

            <Section title={`Ranked opportunities (${run.output.opportunities.length})`} subtitle="Layer 3 · Decision Engine — the product's decision">
              <div className="space-y-3">
                {run.output.opportunities.map((op) => (
                  <details key={op.opportunity_id} open={op.priority_rank <= 3} className="rounded-md border border-border p-3">
                    <summary className="cursor-pointer text-sm font-medium">
                      #{op.priority_rank} · {op.intent_label}{" "}
                      <span className="text-xs text-muted-foreground">
                        ({op.horizon} · impact {op.scores.impact} · effort {op.scores.effort} · conf {op.scores.confidence} · priority {op.scores.numeric.priority_raw.toFixed(2)})
                      </span>
                    </summary>
                    <div className="mt-3 space-y-3 text-sm">
                      <p className="text-foreground/80">{op.intent_description}</p>

                      {op.priority_rank === 1 && op.tradeoffs.ranked_first_because && (
                        <div className="rounded-md bg-muted/50 p-2 text-sm">
                          <span className="font-medium">Ranked first because:</span>{" "}
                          {op.tradeoffs.ranked_first_because}
                        </div>
                      )}

                      {op.tradeoffs.ranked_above.length > 0 && (
                        <div>
                          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Ranked above</p>
                          <ul className="text-xs space-y-0.5">
                            {op.tradeoffs.ranked_above.map((r, i) => (
                              <li key={i}>· {r.opportunity_id} — <span className="font-mono">{r.reason_code}</span> (Δ {r.delta.toFixed(2)})</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <TagList label="Aligned goals" items={op.alignment.matched_goals.map((t) => t.label)} />
                        <TagList label="Aligned strengths" items={op.alignment.matched_strengths.map((t) => t.label)} />
                      </div>

                      <div>
                        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                          Policies fired ({op.policies_fired.length})
                        </p>
                        <ul className="text-xs space-y-0.5">
                          {op.policies_fired.map((f, i) => (
                            <li key={i}>
                              · <span className="font-mono">{f.policy_id}@{f.version}</span> — {describeEffect(f.effect)}
                            </li>
                          ))}
                          {op.policies_fired.length === 0 && (
                            <li className="italic text-muted-foreground">none — pure score</li>
                          )}
                        </ul>
                      </div>

                      <details className="text-xs">
                        <summary className="cursor-pointer text-muted-foreground">Numeric scores</summary>
                        <pre className="mt-1 overflow-x-auto bg-muted/50 p-2 rounded">
{JSON.stringify(op.scores.numeric, null, 2)}
                        </pre>
                      </details>
                    </div>
                  </details>
                ))}
                {run.output.opportunities.length === 0 && (
                  <p className="italic text-muted-foreground text-sm">No opportunities cleared the bar.</p>
                )}
              </div>
            </Section>

            <Section title={`Rejected candidates (${run.output.rejected.length})`} subtitle="Layer 3 · Decision Engine — every rejection has a typed reason">
              <ul className="space-y-2 text-sm">
                {run.output.rejected.map((r) => (
                  <li key={r.candidate_id} className="rounded-md border border-border p-3">
                    <p className="font-medium">{r.intent_label}</p>
                    <p className="text-xs text-muted-foreground">
                      reason: <span className="font-mono">{r.reason_code}</span>
                    </p>
                    <ul className="text-xs mt-2 space-y-0.5">
                      {r.policies_fired.map((f, i) => (
                        <li key={i}>· <span className="font-mono">{f.policy_id}@{f.version}</span> — {describeEffect(f.effect)}</li>
                      ))}
                    </ul>
                  </li>
                ))}
                {run.output.rejected.length === 0 && (
                  <li className="italic text-muted-foreground">none</li>
                )}
              </ul>
            </Section>

            {run.output.insufficient_evidence.length > 0 && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                <p className="text-sm font-medium mb-1">Insufficient evidence</p>
                <ul className="text-xs space-y-1">
                  {run.output.insufficient_evidence.map((n, i) => (
                    <li key={i}>
                      · <span className="font-medium">{n.area}</span> — <span className="font-mono">{n.reason_code}</span>
                      {n.what_would_unblock_it && (
                        <span className="text-muted-foreground"> (unblock: {n.what_would_unblock_it.action})</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-3">
      <div>
        <h2 className="text-lg font-semibold">{title}</h2>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function ContextList({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="space-y-1">
      <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
      <ul className="space-y-0.5">
        {items.map((it, i) => (
          <li key={i} className="text-foreground/80">· {it}</li>
        ))}
        {items.length === 0 && <li className="italic text-muted-foreground">none</li>}
      </ul>
    </div>
  );
}

function TagList({ label, items }: { label: string; items: string[] }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">{label}</p>
      {items.length === 0 ? (
        <p className="italic text-muted-foreground text-xs">none</p>
      ) : (
        <ul className="text-xs space-y-0.5">
          {items.map((it, i) => (
            <li key={i}>· {it}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function describeEffect(e: PolicyEffect): string {
  switch (e.kind) {
    case "reject":
      return `reject (${e.reason_code})`;
    case "boost":
      return `boost +${e.delta} — ${e.reason}`;
    case "downweight":
      return `downweight -${e.delta} — ${e.reason}`;
    case "set_horizon":
      return `set horizon → ${e.horizon} — ${e.reason}`;
  }
}

function PoliciesInPlay() {
  return (
    <Section title={`Decision policies in play (${DEFAULT_POLICIES.length})`} subtitle="Retail Ranker's expertise — versioned + inspectable">
      <ul className="text-xs space-y-1">
        {DEFAULT_POLICIES.map((p) => (
          <li key={p.policy_id}>
            · <span className="font-mono">{p.policy_id}@{p.version}</span> — {p.description}
          </li>
        ))}
      </ul>
    </Section>
  );
}